import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";

const PASSWORD_RULES = [
    { test: (p) => p.length >= 8, label: "Mínimo 8 caracteres" },
    { test: (p) => /[a-z]/.test(p), label: "Una letra minúscula" },
    { test: (p) => /[A-Z]/.test(p), label: "Una letra mayúscula" },
    { test: (p) => /[0-9]/.test(p), label: "Un número" },
    { test: (p) => /[^a-zA-Z0-9]/.test(p), label: "Un carácter especial (!@#$%...)" },
];

const isPasswordValid = (password) => PASSWORD_RULES.every((r) => r.test(password));

const PasswordStrength = ({ password }) => {
    if (!password) return null;
    return (
        <ul className="mt-2 space-y-1">
            {PASSWORD_RULES.map((rule, i) => {
                const passes = rule.test(password);
                return (
                    <li key={i} className={`text-xs flex items-center gap-1 ${passes ? "text-green-600" : "text-gray-400"}`}>
                        {passes ? (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /></svg>
                        )}
                        {rule.label}
                    </li>
                );
            })}
        </ul>
    );
};

const LoginPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const redirectTo = searchParams.get("redirect") || "/listado";
    const rolInicial = searchParams.get("rolInicial") || null;

    const [view, setView] = useState("login"); // login, register, forgot, resetSent
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Login state
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    // Register state
    const [nombre, setNombre] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Forgot password state
    const [forgotEmail, setForgotEmail] = useState("");

    const handleGoogleLogin = () => {
        const rolParam = rolInicial ? `&rolInicial=${encodeURIComponent(rolInicial)}` : '';
        window.location.href = `${window.REACT_APP_API_ENDPOINT}/auth/google?redirect=${encodeURIComponent(redirectTo)}${rolParam}`;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: loginEmail, password: loginPassword }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Email o contraseña incorrectos");

            window.location.href = redirectTo;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(null);

        if (!isPasswordValid(password)) {
            setError("La contraseña no cumple todos los requisitos");
            return;
        }

        if (password !== confirmPassword) {
            setError("Las contraseñas no coinciden");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ nombre, email: regEmail, password, redirect: redirectTo, rolNombre: rolInicial }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error al crear la cuenta");

            if (data.emailVerificationPending) {
                setView("verifyPending");
                return;
            }

            window.location.href = redirectTo;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: forgotEmail }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error al procesar la solicitud");

            setView("resetSent");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const inputClassName = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

    return (
        <div className="container mx-auto px-4 py-12">
            <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">

                {view === "login" && (
                    <>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Iniciar sesión</h2>

                        {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                        <button
                            onClick={handleGoogleLogin}
                            className="w-full flex items-center justify-center bg-[#4285F4] hover:bg-[#357ae8] text-white font-medium py-2.5 px-4 rounded-md shadow-sm mb-4"
                        >
                            <img
                                src="https://developers.google.com/identity/images/g-logo.png"
                                alt="Google"
                                className="h-5 w-5 mr-2 bg-white rounded-full"
                            />
                            Continuar con Google
                        </button>

                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                            <div className="relative flex justify-center text-sm"><span className="bg-white px-3 text-gray-400">o con email</span></div>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                                <input type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className={inputClassName} />
                            </div>
                            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md shadow-sm disabled:opacity-50">
                                {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                            </button>
                        </form>

                        <div className="mt-4 text-center text-sm text-gray-500">
                            <button onClick={() => { setError(null); setView("forgot"); }} className="text-blue-600 hover:underline">
                                ¿Olvidaste tu contraseña?
                            </button>
                        </div>

                        <p className="text-center text-sm text-gray-500 mt-4">
                            ¿No tienes cuenta?{" "}
                            <button onClick={() => { setError(null); setView("register"); }} className="text-blue-600 hover:underline font-medium">
                                Crear cuenta
                            </button>
                        </p>
                    </>
                )}

                {view === "register" && (
                    <>
                        <button onClick={() => { setError(null); setView("login"); }} className="text-blue-600 hover:underline mb-4 text-sm">
                            &larr; Volver
                        </button>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Crear cuenta</h2>

                        {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                        <button
                            onClick={handleGoogleLogin}
                            className="w-full flex items-center justify-center bg-[#4285F4] hover:bg-[#357ae8] text-white font-medium py-2.5 px-4 rounded-md shadow-sm mb-4"
                        >
                            <img
                                src="https://developers.google.com/identity/images/g-logo.png"
                                alt="Google"
                                className="h-5 w-5 mr-2 bg-white rounded-full"
                            />
                            Registrarse con Google
                        </button>

                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                            <div className="relative flex justify-center text-sm"><span className="bg-white px-3 text-gray-400">o con email</span></div>
                        </div>

                        <form onSubmit={handleRegister} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                                <input type="text" required value={nombre} onChange={(e) => setNombre(e.target.value)} className={inputClassName} placeholder="Tu nombre" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClassName} placeholder="Crea una contraseña segura" />
                                <PasswordStrength password={password} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                                <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClassName} />
                            </div>
                            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md shadow-sm disabled:opacity-50">
                                {loading ? "Creando cuenta..." : "Crear cuenta"}
                            </button>
                        </form>

                        <p className="text-center text-sm text-gray-500 mt-4">
                            ¿Ya tienes cuenta?{" "}
                            <button onClick={() => { setError(null); setView("login"); }} className="text-blue-600 hover:underline">
                                Iniciar sesión
                            </button>
                        </p>
                    </>
                )}

                {view === "forgot" && (
                    <>
                        <button onClick={() => { setError(null); setView("login"); }} className="text-blue-600 hover:underline mb-4 text-sm">
                            &larr; Volver
                        </button>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Recuperar contraseña</h2>
                        <p className="text-gray-500 text-sm mb-6">
                            Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.
                        </p>

                        {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                        <form onSubmit={handleForgotPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                            </div>
                            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md shadow-sm disabled:opacity-50">
                                {loading ? "Enviando..." : "Enviar enlace de recuperación"}
                            </button>
                        </form>
                    </>
                )}

                {view === "verifyPending" && (
                    <div className="text-center py-4">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4">
                            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Verifica tu email</h2>
                        <p className="text-gray-500 text-sm mb-2">
                            Hemos enviado un enlace de verificación a <strong>{regEmail}</strong>.
                        </p>
                        <p className="text-gray-500 text-sm mb-6">
                            Revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
                        </p>
                        <button onClick={() => { setError(null); setView("login"); }} className="text-blue-600 hover:underline text-sm">
                            Volver a iniciar sesión
                        </button>
                    </div>
                )}

                {view === "resetSent" && (
                    <div className="text-center py-4">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                            <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Revisa tu email</h2>
                        <p className="text-gray-500 text-sm mb-6">
                            Si existe una cuenta con ese email, recibirás un enlace para restablecer tu contraseña.
                        </p>
                        <button onClick={() => { setError(null); setView("login"); }} className="text-blue-600 hover:underline text-sm">
                            Volver a iniciar sesión
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
